import { router } from "@inertiajs/react"
import { useEffect } from "react"
import { toast } from "sonner"
import { Toaster } from "@/components/ui/sonner"
import type { FlashData } from "@/types"

interface Props {
  initialFlash: FlashData
}

// Fixed ids: a visit can fire several events carrying the same flash (navigate + success) - a
// toast with an existing id is updated in place instead of stacking a duplicate.
const NOTICE_ID = "flash-notice"
const ALERT_ID = "flash-alert"
const NOTICE_DURATION_MS = 5000

function showFlash(flash: FlashData | undefined) {
  if (flash?.notice) {
    toast.success(flash.notice, { id: NOTICE_ID, duration: NOTICE_DURATION_MS })
  }
  if (flash?.alert) {
    // An alert stays until it's closed - it usually asks the admin to do something.
    toast.error(flash.alert, { id: ALERT_ID, duration: Infinity })
  }
}

// The admin's notice/alert for Inertia pages, as toasts fixed to the viewport - visible however
// far the page is scrolled (e.g. after "Save" at the bottom of a long form). The ERB banner only
// renders on a full page load, so after an Inertia visit the flash was never shown at all.
export function InertiaFlash({ initialFlash }: Props) {
  useEffect(() => {
    showFlash(initialFlash)
    // "navigate" alone isn't enough: Inertia skips it when a visit lands on the same URL (a form
    // save redirecting back to its own edit page), which still fires "success". A failed
    // validation fires "error" - the previous visit's notice no longer applies.
    const unsubscribers = [
      router.on("navigate", (event) => showFlash(event.detail.page.flash)),
      router.on("success", (event) => showFlash(event.detail.page.flash)),
      router.on("error", () => toast.dismiss(NOTICE_ID)),
    ]
    return () => unsubscribers.forEach((unsubscribe) => unsubscribe())
  }, [initialFlash])

  return <Toaster position="top-right" richColors closeButton />
}
